import {
  LIVE_TRANSCRIPTION_PARAGRAPH_GAP_SECONDS,
  LiveTranscriptRole,
  LiveTranscriptSegment,
  LiveTranscriptSegmentWithDisplayMeta,
  UserFinishedEventPayload,
  UserSegmentEventPayload,
} from 'src/components/Assistant/live-transcript/live-transcript.types';

export const matchUserSegmentEvent = (
  eventData: unknown,
  userId: string,
): UserSegmentEventPayload | undefined => {
  const segmentEvent = eventData as UserSegmentEventPayload | undefined;

  if (
    segmentEvent?.type !== 'RealTimeUserTranscriptionSegment' ||
    segmentEvent.userId !== userId
  ) {
    return undefined;
  }

  return segmentEvent;
};

export const matchUserFinishedEvent = (
  eventData: unknown,
  userId: string,
): UserFinishedEventPayload | undefined => {
  const finishedEvent = eventData as UserFinishedEventPayload | undefined;

  if (
    finishedEvent?.type !== 'RealTimeUserTranscriptionFinished' ||
    finishedEvent.userId !== userId
  ) {
    return undefined;
  }

  return finishedEvent;
};

export const buildSegmentKey = (input: {
  externalTranscriptionId: string;
  timestamp: string;
  role: LiveTranscriptRole;
  transcriptionText: string;
}): string =>
  [input.externalTranscriptionId, input.timestamp, input.role, input.transcriptionText].join(
    '|',
  );

const toComparableTimestamp = (timestamp: string): number => {
  const value = Date.parse(timestamp);

  return Number.isNaN(value) ? 0 : value;
};

export const mergeSegmentsByOrder = (
  existing: LiveTranscriptSegment[],
  incoming: LiveTranscriptSegment[],
): LiveTranscriptSegment[] =>
  [...existing, ...incoming].sort((a, b) => {
    const byTime = toComparableTimestamp(a.timestamp) - toComparableTimestamp(b.timestamp);

    if (byTime !== 0) {
      return byTime;
    }

    return (a.start ?? 0) - (b.start ?? 0);
  });

const hasTiming = (
  segment: LiveTranscriptSegment,
): segment is LiveTranscriptSegment & { start: number; duration: number } =>
  typeof segment.start === 'number' && typeof segment.duration === 'number';

export const groupSegmentsIntoParagraphs = (
  segments: LiveTranscriptSegment[],
): LiveTranscriptSegment[] => {
  const grouped: LiveTranscriptSegment[] = [];

  for (const segment of segments) {
    const previous = grouped[grouped.length - 1];

    if (
      previous &&
      previous.role === segment.role &&
      hasTiming(previous) &&
      hasTiming(segment) &&
      segment.start - (previous.start + previous.duration) <=
        LIVE_TRANSCRIPTION_PARAGRAPH_GAP_SECONDS
    ) {
      grouped[grouped.length - 1] = {
        ...previous,
        transcriptionText: `${previous.transcriptionText} ${segment.transcriptionText}`,
        duration: segment.start + segment.duration - previous.start,
      };
      continue;
    }

    grouped.push(segment);
  }

  return grouped;
};

export const mapSegmentsWithSpeakerChanges = (
  segments: LiveTranscriptSegment[],
): LiveTranscriptSegmentWithDisplayMeta[] =>
  segments.map((segment, index) => {
    if (index === 0) {
      return { segment, showSpeakerChange: true };
    }

    const previous = segments[index - 1];

    return {
      segment,
      showSpeakerChange: previous.role !== segment.role,
    };
  });

export const buildFullTranscriptText = (segments: LiveTranscriptSegment[]): string =>
  segments
    .map((segment) => {
      const roleLabel = segment.role === LiveTranscriptRole.Agent ? 'Agent' : 'Client';

      return `${roleLabel}: ${segment.transcriptionText}`;
    })
    .join('\n');

export const countMatchesInText = (text: string, query: string): number => {
  if (!query) {
    return 0;
  }

  const sourceText = text.toLowerCase();
  const searchPattern = query.toLowerCase();
  let count = 0;
  let cursor = 0;

  while (cursor <= sourceText.length - searchPattern.length) {
    const found = sourceText.indexOf(searchPattern, cursor);

    if (found === -1) {
      break;
    }

    count += 1;
    cursor = found + searchPattern.length;
  }

  return count;
};

export interface TranscriptSearchIndex {
  totalMatches: number;
  matchOffsetBySegmentId: Map<string, number>;
}

export const buildTranscriptSearchIndex = (
  segments: LiveTranscriptSegment[],
  query: string,
): TranscriptSearchIndex => {
  const matchOffsetBySegmentId = new Map<string, number>();

  if (!query) {
    return { totalMatches: 0, matchOffsetBySegmentId };
  }

  let totalMatches = 0;

  for (const segment of segments) {
    matchOffsetBySegmentId.set(segment.id, totalMatches);
    totalMatches += countMatchesInText(segment.transcriptionText, query);
  }

  return { totalMatches, matchOffsetBySegmentId };
};
